import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import {
    useJwtToken,
    API_ENDPOINT,
    useVerifyAdmin,
    useVerifyCustomer,
} from 'helper';
import { columns as initialColumns, PAGE_SIZE } from './constants';
export const useGetSites = (
    filterBy,
    search,
    sortField,
    sortDirection,
    currentPage
) => {
    const accessToken = useJwtToken();
    const isAdmin = useVerifyAdmin();
    const sitesResponse = useQuery(
        ['sites', filterBy, search, sortField, sortDirection, currentPage],
        () =>
            getSites(
                filterBy,
                search,
                sortField,
                sortDirection,
                currentPage,
                accessToken,
                isAdmin
            ),
        {
            keepPreviousData: true, // Keep previous page data while fetching the next page
            staleTime: 5 * 60 * 1000, // Data is considered fresh for 5 minutes
            retry: 2,
            enabled: !!accessToken, // Only run the query if accessToken is available
        }
    );
    return sitesResponse;
};

export const getSites = async (
    filterBy,
    search,
    sortField,
    sortDirection,
    currentPage,
    accessToken,
    isAdmin = false
) => {
    const url = new URL(`${API_ENDPOINT}/sites`);
    const sortOrder = sortDirection === 1 ? 'asc' : 'desc';

    if (filterBy && search) {
        url.searchParams.append(`${filterBy}`, search);
    }
    url.searchParams.append('sortField', sortField ?? 'Name');
    url.searchParams.append('sortOrder', sortOrder);
    url.searchParams.append('from', (currentPage - 1) * PAGE_SIZE);
    url.searchParams.append('size', PAGE_SIZE);
    url.searchParams.append('archived', isAdmin ? 'true' : 'false');

    try {
        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                Authorization: accessToken,
            },
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Unknown error occurred');
        }

        const data = await response.json();

        if (!data || !data.sites) {
            throw new Error('No sites data found');
        }
        return {
            sites: data.sites,
            total: data.total,
        };
    } catch (error) {
        console.error('Error fetching sites:', error.message);
        throw new Error(error.message || 'Failed to fetch sites');
    }
};

export const useColumns = () => {
    const isCustomer = useVerifyCustomer();
    const [columns, setColumns] = useState([...initialColumns]);

    useEffect(() => {
        if (isCustomer) {
            // Exclude optional columns for customers
            setColumns([
                ...initialColumns.filter(
                    (col) =>
                        col.accessor !== 'CustomerEmail' &&
                        col.accessor !== 'CustomerName'
                ),
            ]);
        }
    }, [isCustomer]);

    return columns;
};
